/**
 * The date presets the filter builder offers, and the recognizer that reads
 * them back.
 *
 * A date filter is almost never a comparison. People ask for "this week",
 * "overdue", "next 7 days" — periods with two ends — and the builder used to
 * answer with five relative literals and a calendar. This module is the menu
 * they actually want, plus the one piece of machinery a period needs: a
 * CALENDAR SPAN compiles to two conditions (`due >= 2026-09-08 and
 * due < 2026-09-15`), because that is what the query engine understands, and
 * the recognizer reads that pair back as ONE row and ONE chip ("Due: This
 * week"). Nothing new goes into the grammar, nothing is stored that a hand-
 * typed query could not say, and a pair the recognizer does not know stays two
 * plain conditions — never rewritten, never hidden.
 *
 * WHY ABSOLUTE BOUNDS FOR CALENDAR SPANS. "This week" is a fact about the
 * calendar on the day it was picked, and the URL is a link somebody shares.
 * Compiling it to `>= 2026-09-08 and < 2026-09-15` means the link still says
 * what it said; the chip reads "This week" only while that is still true, and
 * names the dates once it is not. The ROLLING periods are the opposite kind of
 * question ("anything in the last 30 days") and stay relative, which is why
 * they keep the grammar's own literals — including the forward `+7d` that
 * "Next 7 days" needs.
 *
 * Everything here is pure. `now` is injectable so a test can stand on a fixed
 * day, and the UI's own `dayjs()` is the default.
 */
import dayjs from 'dayjs';

import { date as makeDate, rel as makeRel } from './kql';
import { formatShortDate } from './dateDisplay';
import { SPAN_ROW, isNullaryOperator, newRowId, pathKey } from './kqlBuilder';

/** The exact-date modes, which open a control instead of committing a value. */
export const PRESET_BETWEEN = 'between';
export const PRESET_SPECIFIC = 'specific';
export const PRESET_TYPED = 'typed';

const startOfDay = now => dayjs(now).startOf('day');

const spanOf = (from, to) => [
	{ cmp: 'gte', value: makeDate(from.format('YYYY-MM-DD')) },
	// The upper bound is EXCLUSIVE, so a span never depends on whether the
	// stored value carries a time: "this week" ends where next week begins.
	{ cmp: 'lt', value: makeDate(to.format('YYYY-MM-DD')) },
];

/**
 * The menu, in the order it is offered. Each entry is one of:
 *   `span(now)`   a calendar period → two absolute conditions
 *   `parts()`     a fixed pair of relative conditions (the forward periods)
 *   `point`       one relative literal, used with the row's own operator
 *   neither       a mode the UI opens a control for (Between…, Specific…)
 */
export const DATE_PRESETS = [
	{ value: 'today', label: 'Today', group: 'Periods', span: now => spanOf(startOfDay(now), startOfDay(now).add(1, 'day')) },
	{ value: 'yesterday', label: 'Yesterday', group: 'Periods', span: now => spanOf(startOfDay(now).subtract(1, 'day'), startOfDay(now)) },
	{ value: 'this-week', label: 'This week', group: 'Periods', span: now => spanOf(dayjs(now).startOf('week'), dayjs(now).startOf('week').add(1, 'week')) },
	{ value: 'last-week', label: 'Last week', group: 'Periods', span: now => spanOf(dayjs(now).startOf('week').subtract(1, 'week'), dayjs(now).startOf('week')) },
	{ value: 'this-month', label: 'This month', group: 'Periods', span: now => spanOf(dayjs(now).startOf('month'), dayjs(now).startOf('month').add(1, 'month')) },
	{ value: 'last-month', label: 'Last month', group: 'Periods', span: now => spanOf(dayjs(now).startOf('month').subtract(1, 'month'), dayjs(now).startOf('month')) },
	{ value: 'this-year', label: 'This year', group: 'Periods', span: now => spanOf(dayjs(now).startOf('year'), dayjs(now).startOf('year').add(1, 'year')) },

	{ value: 'last-7d', label: 'Last 7 days', group: 'Rolling', point: '-7d' },
	{ value: 'last-30d', label: 'Last 30 days', group: 'Rolling', point: '-30d' },
	{ value: 'last-3mo', label: 'Last 3 months', group: 'Rolling', point: '-3mo' },
	{ value: 'last-1y', label: 'Last year', group: 'Rolling', point: '-1y' },
	{
		value: 'next-7d',
		label: 'Next 7 days',
		group: 'Rolling',
		parts: () => [
			{ cmp: 'gte', value: makeRel('today') },
			{ cmp: 'lt', value: makeRel('+7d') },
		],
	},
	{
		value: 'next-30d',
		label: 'Next 30 days',
		group: 'Rolling',
		parts: () => [
			{ cmp: 'gte', value: makeRel('today') },
			{ cmp: 'lt', value: makeRel('+30d') },
		],
	},
	{
		value: 'overdue',
		label: 'Overdue',
		group: 'Rolling',
		// Past AND filled in: a date nobody entered is not late, it is absent,
		// and a query that says otherwise buries every blank row in the list.
		parts: () => [
			{ cmp: 'lt', value: makeRel('today') },
			{ cmp: 'nempty' },
		],
	},

	{ value: PRESET_BETWEEN, label: 'Between…', group: 'Exact' },
	{ value: PRESET_SPECIFIC, label: 'Specific date…', group: 'Exact' },
	{ value: PRESET_TYPED, label: 'Type a date…', group: 'Exact' },
];

const PRESETS_BY_VALUE = new Map(DATE_PRESETS.map(preset => [preset.value, preset]));

export const datePreset = value => PRESETS_BY_VALUE.get(value) || null;

/** The preset menu as antd option groups, in the declared order. */
export const datePresetOptions = () => {
	const groups = [];
	DATE_PRESETS.forEach(preset => {
		let group = groups.find(entry => entry.label === preset.group);
		if (!group) { group = { label: preset.group, options: [] }; groups.push(group); }
		group.options.push({ value: preset.value, label: preset.label });
	});
	return groups;
};

/** A preset that commits two conditions at once (a period), or null. */
export function presetParts(value, now) {
	const preset = datePreset(value);
	if (!preset) return null;
	if (typeof preset.span === 'function') return preset.span(now === undefined ? dayjs() : dayjs(now));
	if (typeof preset.parts === 'function') return preset.parts();
	return null;
}

/** A preset that commits ONE relative literal (used with the row's operator). */
export const presetPoint = value => datePreset(value)?.point || null;

/** True when the preset opens a control rather than committing a value. */
export const isModePreset = value => (
	value === PRESET_BETWEEN || value === PRESET_SPECIFIC || value === PRESET_TYPED
);

// ------------------------------------------------------------- recognition

const partKey = part => {
	if (!part) return '';
	if (isNullaryOperator(part.cmp)) return `${part.cmp}:`;
	if (!part.value) return `${part.cmp}:?`;
	return `${part.cmp}:${part.value.type}:${String(part.value.v).toLowerCase()}`;
};

const pairKey = parts => (parts || []).map(partKey).join('&');

// A facet's card key can spell an element's name; only the flag tells them
// apart, so a period is never recognized across the two.
const samePath = (a, b) => (
	pathKey(a?.path, !!a?.forceElement, !!a?.facet) === pathKey(b?.path, !!b?.forceElement, !!b?.facet)
);

/** "Sep 1 – Sep 30" for a `>= A and <= B` pair; the `<` form loses its last day. */
function boundsLabel(lower, upper, upperCmp) {
	const from = dayjs(lower);
	const toRaw = dayjs(upper);
	if (!from.isValid() || !toRaw.isValid()) return null;
	const to = upperCmp === 'lt' ? toRaw.subtract(1, 'day') : toRaw;
	const fromText = formatShortDate(from.format('YYYY-MM-DD'));
	const toText = formatShortDate(to.format('YYYY-MM-DD'));
	if (!fromText || !toText) return null;
	return fromText === toText ? fromText : `${fromText} – ${toText}`;
}

/**
 * Two adjacent top-level conditions read as ONE period, or null.
 *
 * The shape is deliberately narrow — the same field, a `>=` lower bound and a
 * `<` / `<=` upper one (plus the one exception the Overdue preset writes,
 * `< today and is not empty`). A pair that matches a preset exactly wears the
 * preset's name; any other pair of date literals reads as its own bounds
 * ("Sep 1 – Sep 30"), which is what makes Between… a single chip. Everything
 * else stays two conditions, because a filter nobody can decompose is worse
 * than one extra chip.
 */
export function recognizeSpan(first, second, now) {
	if (!first || !second || first.op === 'and' || second.op === 'and') return null;
	if (!samePath(first, second)) return null;
	const parts = [
		{ cmp: first.cmp, value: first.value },
		{ cmp: second.cmp, value: second.value },
	];
	const key = pairKey(parts);

	const matched = DATE_PRESETS.find(preset => {
		const compiled = presetParts(preset.value, now);
		return compiled && pairKey(compiled) === key;
	});
	if (matched) return { preset: matched.value, label: matched.label, parts };

	if (first.cmp !== 'gte') return null;
	if (second.cmp !== 'lt' && second.cmp !== 'lte') return null;
	if (first.value?.type !== 'date' || second.value?.type !== 'date') return null;
	const label = boundsLabel(first.value.v, second.value.v, second.cmp);
	if (!label) return null;
	return { preset: PRESET_BETWEEN, label, parts };
}

/**
 * Top-level conditions grouped for display: each entry covers one condition,
 * or the two a recognized period spans. `indices` are positions in the ORIGINAL
 * list, so dismissing a chip still removes exactly what it stands for.
 */
export function groupSpanConditions(conditions, now) {
	const groups = [];
	for (let i = 0; i < (conditions || []).length; i += 1) {
		const span = i + 1 < conditions.length
			? recognizeSpan(conditions[i], conditions[i + 1], now)
			: null;
		if (span) {
			groups.push({ indices: [i, i + 1], node: conditions[i], span });
			i += 1;
		} else {
			groups.push({ indices: [i], node: conditions[i], span: null });
		}
	}
	return groups;
}

/** Builder rows with every recognized pair collapsed into one span row. */
export function collapseSpanRows(rows, now) {
	if (!Array.isArray(rows)) return rows;
	// A repeating group's `has` holds rows of its own (KPR-683): a period on
	// one of its columns is one row there too.
	const source = rows.map(row => (Array.isArray(row?.entryRows) ? { ...row, entryRows: collapseSpanRows(row.entryRows, now) } : row));
	const out = [];
	for (let i = 0; i < source.length; i += 1) {
		const span = i + 1 < source.length ? recognizeSpan(source[i], source[i + 1], now) : null;
		if (span) {
			out.push(spanRow(source[i], span.preset, span.parts, { id: source[i].id }));
			i += 1;
		} else {
			out.push(source[i]);
		}
	}
	return out;
}

/** A span row on a field, carrying the parts it will serialize to. */
export function spanRow(field, preset, parts, { id } = {}) {
	return {
		id: id || newRowId(),
		kind: SPAN_ROW,
		path: [...(field?.path || [])],
		forceElement: !!field?.forceElement,
		facet: !!field?.facet,
		preset,
		parts: parts || [],
	};
}

/** The words a span row (or a recognized pair) reads as: "This week". */
export function spanLabel(row, now) {
	const direct = datePreset(row?.preset);
	const recognized = recognizeSpan(
		{ path: row?.path, forceElement: row?.forceElement, cmp: row?.parts?.[0]?.cmp, value: row?.parts?.[0]?.value },
		{ path: row?.path, forceElement: row?.forceElement, cmp: row?.parts?.[1]?.cmp, value: row?.parts?.[1]?.value },
		now
	);
	if (recognized) return recognized.label;
	return direct ? direct.label : '';
}

/**
 * One end of a Between… span, from the day picked for it (`'YYYY-MM-DD'`), or
 * undefined when nothing valid is picked. Both ends are DAYS, and the upper one
 * is EXCLUSIVE (`< the day after`), exactly as every calendar period above: an
 * inclusive `<= 2026-09-30` reads correctly on a `date` element and silently
 * drops the last day on a `date-time` one, where '2026-09-30T09:00' sorts AFTER
 * '2026-09-30'. The filter builder's Between… and a column header's custom
 * range (KPR-1712) both write through this, so the two cannot drift.
 */
export function betweenPart(index, stored) {
	const day = stored ? dayjs(stored) : null;
	if (!day || !day.isValid()) return undefined;
	return index === 0
		? { cmp: 'gte', value: makeDate(day.format('YYYY-MM-DD')) }
		: { cmp: 'lt', value: makeDate(day.add(1, 'day').format('YYYY-MM-DD')) };
}

/** The day a value falls on, as the pair of conditions that names that day. */
export function dayParts(value) {
	const day = dayjs(value);
	if (!day.isValid()) return null;
	return spanOf(day.startOf('day'), day.startOf('day').add(1, 'day'));
}
